import React from 'react';
import { get as _get } from 'lodash';
import { SocialPost } from '../../types';
import { COMMENT_TYPES } from '../../constants';
import Icon from '../common/Icon';
import ReadMoreText from '../common/ReadMoreText';

interface SocialPostCardProps {
  post: SocialPost;
  profileName: string;
  isSelected: boolean;
  onSelect: () => void;
  onCopy: (text: string) => void;
  onShare: (post: SocialPost) => void;
  onGenerateComment: (type: 'insightful' | 'supportive' | 'question') => void;
}

const SocialPostCard: React.FC<SocialPostCardProps> = ({
  post,
  profileName,
  isSelected,
  onSelect,
  onCopy,
  onShare,
  onGenerateComment,
}) => {
  const content = _get(post, 'content', '');
  const date = _get(post, 'date', '');
  
  if (!content) return null;

  return (
    <div
      onClick={onSelect}
      className={`border p-8 rounded-brand cursor-pointer transition-all relative overflow-hidden transform ${
        isSelected
          ? 'border-brand-pink bg-brand-pink/5 scale-[1.02] shadow-xl ring-1 ring-brand-pink/20'
          : 'border-brand-grey01 bg-white hover:border-brand-aqua hover:scale-[1.01]'
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-black text-white rounded-full flex items-center justify-center font-bold text-sm rotate-[-5deg]">
            RD
          </div>
          <div>
            <p className="font-bold text-sm">{profileName}</p>
            {date && (
              <p className="text-[10px] text-brand-grey02 font-black uppercase">
                {date}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy(content);
            }}
            className="w-10 h-10 bg-brand-grey01 hover:bg-brand-aqua/20 rounded-full flex items-center justify-center transition-colors group/copy"
            title="Copy text"
          >
            <Icon
              name="content_copy"
              size="sm"
              className="text-brand-grey08 group-hover/copy:text-brand-black"
            />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare(post);
            }}
            className="w-10 h-10 bg-brand-grey01 hover:bg-brand-pink/20 rounded-full flex items-center justify-center transition-colors group/share"
            title="Share post"
          >
            <Icon
              name="share"
              size="sm"
              className="text-brand-grey08 group-hover/share:text-brand-pink"
            />
          </button>
        </div>
      </div>

      <p
        className={`font-medium italic leading-relaxed transition-all duration-500 ${
          isSelected ? 'text-2xl text-brand-black' : 'text-base text-brand-grey08'
        }`}
      >
        <ReadMoreText text={content} charLimit={350} />
      </p>

      <div className="mt-8 pt-6 border-t border-brand-grey01 flex flex-wrap gap-2">
        {(COMMENT_TYPES || []).map(({ id, label }) => (
          <button
            key={id}
            onClick={(e) => {
              e.stopPropagation();
              onGenerateComment(id);
            }}
            className="bg-brand-black text-white px-4 py-2 rounded-pill text-[9px] font-black uppercase tracking-widest hover:bg-brand-pink transition-all shadow-md active:scale-95"
          >
            Generate {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SocialPostCard;
